import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { PageHeader } from "../../components/Layout";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, X, CheckCircle, WarningCircle, Clock, Coin } from "@phosphor-icons/react";

const emptyForm = {
  name: "",
  code: "",
  valid_from: "",
  valid_until: "",
  is_active: true,
  contact_email: "",
  notes: "",
  admin_email: "",
  admin_password: "",
  admin_name: "",
};

function validityStatus(inst) {
  const today = new Date().toISOString().slice(0, 10);
  if (inst.is_active === false) return { label: "Inactive", tone: "zinc" };
  if (inst.valid_until && inst.valid_until < today) return { label: "Expired", tone: "red" };
  if (inst.valid_from && inst.valid_from > today) return { label: "Scheduled", tone: "amber" };
  return { label: "Active", tone: "emerald" };
}

const toneClasses = {
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber: "bg-amber-50 text-amber-800 border-amber-200",
  red: "bg-red-50 text-red-700 border-red-200",
  zinc: "bg-zinc-100 text-zinc-700 border-zinc-300",
};

export default function InstitutionsList() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [creditInst, setCreditInst] = useState(null);

  const load = () => api.get("/institutions").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    let active = 0, expired = 0, inactive = 0;
    items.forEach((i) => {
      const s = validityStatus(i);
      if (s.label === "Active" || s.label === "Scheduled") active++;
      else if (s.label === "Expired") expired++;
      else inactive++;
    });
    return { total: items.length, active, expired, inactive };
  }, [items]);

  const create = async () => {
    if (!form.name || !form.code) { toast.error("Name and code required"); return; }
    if (form.valid_from && form.valid_until && form.valid_from > form.valid_until) {
      toast.error("Valid from must be before valid until");
      return;
    }
    // Admin creds validation
    const wantsAdmin = form.admin_email || form.admin_password;
    if (wantsAdmin) {
      if (!form.admin_email || !form.admin_password) {
        toast.error("Both admin email and password are required to create a login");
        return;
      }
      if (form.admin_password.length < 8) {
        toast.error("Admin password must be at least 8 characters");
        return;
      }
    }
    setCreating(true);
    try {
      const r = await api.post("/institutions", {
        ...form,
        valid_from: form.valid_from || null,
        valid_until: form.valid_until || null,
        contact_email: form.contact_email || "",
        admin_email: form.admin_email ? form.admin_email.trim().toLowerCase() : null,
        admin_password: form.admin_password || null,
        admin_name: form.admin_name || null,
      });
      if (r.data.admin_user) {
        toast.success(`Institution created with admin login: ${r.data.admin_user.email}`, { duration: 6000 });
      } else {
        toast.success("Institution created");
      }
      setForm(emptyForm);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create"); }
    finally { setCreating(false); }
  };

  const startEdit = (inst) => {
    setEditingId(inst.institution_id);
    setEditForm({
      name: inst.name || "",
      code: inst.code || "",
      valid_from: inst.valid_from || "",
      valid_until: inst.valid_until || "",
      is_active: inst.is_active !== false,
      contact_email: inst.contact_email || "",
      notes: inst.notes || "",
    });
  };

  const saveEdit = async () => {
    if (!editForm.name || !editForm.code) { toast.error("Name and code required"); return; }
    if (editForm.valid_from && editForm.valid_until && editForm.valid_from > editForm.valid_until) {
      toast.error("Valid from must be before valid until");
      return;
    }
    setSavingEdit(true);
    try {
      await api.put(`/institutions/${editingId}`, {
        ...editForm,
        valid_from: editForm.valid_from || null,
        valid_until: editForm.valid_until || null,
      });
      toast.success("Institution updated");
      setEditingId(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
    finally { setSavingEdit(false); }
  };

  const doDelete = async (iid) => {
    setDeletingId(iid);
    try {
      const { data } = await api.delete(`/institutions/${iid}`);
      if (data?.soft_deleted) {
        toast.success(data.message || "Institution deactivated (had associated data)");
      } else {
        toast.success("Institution deleted");
      }
      setConfirmId(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
    finally { setDeletingId(null); }
  };

  return (
    <div>
      <PageHeader overline="Platform" title="Institutions" subtitle="Manage tenant institutions, validity periods, and access." />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="institutions-stats">
        <StatBox label="Total" value={stats.total} icon={<CheckCircle size={16} />} />
        <StatBox label="Active" value={stats.active} tone="emerald" icon={<CheckCircle size={16} />} />
        <StatBox label="Expired" value={stats.expired} tone="red" icon={<WarningCircle size={16} />} />
        <StatBox label="Inactive" value={stats.inactive} tone="zinc" icon={<Clock size={16} />} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-10">
        {/* List */}
        <div className="lg:col-span-2 border border-zinc-200" data-testid="institutions-list">
          {items.length === 0 ? (
            <div className="p-10 text-center text-sm text-zinc-500">No institutions yet.</div>
          ) : items.map((inst, i) => {
            const status = validityStatus(inst);
            const isEditing = editingId === inst.institution_id;
            return (
              <div key={inst.institution_id} className={`p-5 ${i < items.length - 1 ? "border-b border-zinc-200" : ""}`} data-testid={`inst-row-${inst.institution_id}`}>
                {!isEditing ? (
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-medium">{inst.name}</div>
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${toneClasses[status.tone]}`}>{status.label}</span>
                      </div>
                      <div className="text-xs text-zinc-500 mt-1 font-mono">Code: {inst.code}</div>
                      <div className="text-xs text-zinc-500 mt-1">
                        Valid: <span className="font-mono">{inst.valid_from || "—"}</span> → <span className="font-mono">{inst.valid_until || "—"}</span>
                      </div>
                      {inst.contact_email ? <div className="text-xs text-zinc-500 mt-1">{inst.contact_email}</div> : null}
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        <div className="inline-flex items-center gap-1 border border-amber-300 bg-amber-50 text-amber-800 px-2 py-1 text-xs" data-testid={`inst-balance-${inst.institution_id}`}>
                          <Coin size={11} weight="fill" className="text-amber-500" />
                          <span className="font-mono font-bold">{(inst.credits || 0).toLocaleString()}</span>
                          <span className="opacity-70">coins</span>
                        </div>
                      </div>
                      <div className="text-[10px] text-zinc-400 mt-1 font-mono">{inst.institution_id}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => setCreditInst(inst)}
                        className="px-3 py-1.5 text-xs border border-amber-500 text-amber-800 hover:bg-amber-50 inline-flex items-center gap-1.5"
                        data-testid={`inst-credit-btn-${inst.institution_id}`}
                      >
                        <Coin size={12} weight="fill" /> Add coins
                      </button>
                      <button
                        onClick={() => startEdit(inst)}
                        className="px-3 py-1.5 text-xs border border-zinc-300 hover:border-zinc-900 inline-flex items-center gap-1.5"
                        data-testid={`inst-edit-btn-${inst.institution_id}`}
                      >
                        <PencilSimple size={12} /> Edit
                      </button>
                      <button
                        onClick={() => setConfirmId(inst.institution_id)}
                        className="px-3 py-1.5 text-xs border border-red-300 text-red-700 hover:bg-red-50 inline-flex items-center gap-1.5"
                        data-testid={`inst-delete-btn-${inst.institution_id}`}
                      >
                        <Trash size={12} /> Delete
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3" data-testid={`inst-edit-form-${inst.institution_id}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Editing</div>
                      <button onClick={() => setEditingId(null)} className="text-zinc-500 hover:text-zinc-900" data-testid={`inst-edit-cancel-${inst.institution_id}`}>
                        <X size={16} />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <LabeledInput label="Name" value={editForm.name} onChange={(v) => setEditForm({ ...editForm, name: v })} testId="inst-edit-name" />
                      <LabeledInput label="Code" value={editForm.code} onChange={(v) => setEditForm({ ...editForm, code: v.toUpperCase() })} mono testId="inst-edit-code" />
                      <LabeledInput type="date" label="Valid from" value={editForm.valid_from} onChange={(v) => setEditForm({ ...editForm, valid_from: v })} testId="inst-edit-valid-from" />
                      <LabeledInput type="date" label="Valid until" value={editForm.valid_until} onChange={(v) => setEditForm({ ...editForm, valid_until: v })} testId="inst-edit-valid-until" />
                      <LabeledInput label="Contact email" value={editForm.contact_email} onChange={(v) => setEditForm({ ...editForm, contact_email: v })} testId="inst-edit-email" />
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Status</div>
                        <label className="inline-flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={editForm.is_active}
                            onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                            data-testid="inst-edit-active"
                          />
                          Active
                        </label>
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Notes</div>
                      <textarea
                        value={editForm.notes}
                        onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                        rows={2}
                        className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
                        data-testid="inst-edit-notes"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={saveEdit}
                        disabled={savingEdit}
                        className="px-4 py-2 bg-zinc-900 text-white text-sm hover:bg-zinc-800 disabled:opacity-50"
                        data-testid={`inst-edit-save-${inst.institution_id}`}
                      >
                        {savingEdit ? "Saving…" : "Save changes"}
                      </button>
                      <button onClick={() => setEditingId(null)} className="px-4 py-2 border border-zinc-300 text-sm hover:border-zinc-900">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Create */}
        <div className="border border-zinc-200 p-6 h-fit">
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-4 font-semibold">Create new</div>
          <div className="space-y-3">
            <input
              placeholder="Institution name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="inst-name-input"
            />
            <input
              placeholder="Code (e.g. ACME)"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              className="w-full border border-zinc-300 px-3 py-2 text-sm font-mono focus:border-zinc-900 focus:outline-none"
              data-testid="inst-code-input"
            />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Valid from</div>
                <input
                  type="date"
                  value={form.valid_from}
                  onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
                  className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
                  data-testid="inst-valid-from-input"
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Valid until</div>
                <input
                  type="date"
                  value={form.valid_until}
                  onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                  className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
                  data-testid="inst-valid-until-input"
                />
              </div>
            </div>
            <input
              placeholder="Contact email (optional)"
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="inst-email-input"
            />
            <textarea
              placeholder="Notes (optional)"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="inst-notes-input"
            />
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                data-testid="inst-active-input"
              />
              Active
            </label>

            {/* Institution admin login */}
            <div className="border-t border-zinc-200 pt-3 mt-2">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2 font-semibold">Institution admin login</div>
              <input
                placeholder="Admin name (optional)"
                value={form.admin_name}
                onChange={(e) => setForm({ ...form, admin_name: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none mb-2"
                data-testid="inst-admin-name-input"
              />
              <input
                type="email"
                placeholder="admin@institution.com"
                value={form.admin_email}
                onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none mb-2"
                data-testid="inst-admin-email-input"
              />
              <input
                type="password"
                placeholder="Password (min 8 chars)"
                value={form.admin_password}
                onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
                data-testid="inst-admin-password-input"
              />
              <div className="text-[10px] text-zinc-500 mt-1">Leave blank to skip creating a login — you can add one later from the Users page.</div>
            </div>

            <button
              onClick={create}
              disabled={creating}
              className="w-full bg-zinc-900 text-white py-2.5 text-sm font-medium hover:bg-zinc-800 inline-flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="inst-create-btn"
            >
              <Plus size={14} /> {creating ? "Creating…" : "Create institution"}
            </button>
          </div>
        </div>
      </div>

      {/* Delete confirm */}
      {confirmId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="inst-delete-confirm">
          <div className="bg-white max-w-md w-full p-6 border border-zinc-200">
            <div className="text-lg font-semibold mb-2">Delete institution?</div>
            <div className="text-sm text-zinc-600 mb-5">
              This action cannot be undone. If the institution has users or exams, it will be deactivated instead of hard-deleted.
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmId(null)}
                className="px-4 py-2 border border-zinc-300 text-sm hover:border-zinc-900"
                data-testid="inst-delete-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => doDelete(confirmId)}
                disabled={deletingId === confirmId}
                className="px-4 py-2 bg-red-600 text-white text-sm hover:bg-red-700 disabled:opacity-50"
                data-testid="inst-delete-confirm-btn"
              >
                {deletingId === confirmId ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Credit institution modal */}
      {creditInst && (
        <InstitutionCreditModal
          inst={creditInst}
          onClose={() => setCreditInst(null)}
          onSaved={() => { setCreditInst(null); load(); }}
        />
      )}
    </div>
  );
}

function InstitutionCreditModal({ inst, onClose, onSaved }) {
  const [amount, setAmount] = useState(1000);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!amount || amount <= 0) { toast.error("Amount must be positive"); return; }
    setSaving(true);
    try {
      const r = await api.post("/wallet/institution-credit", { institution_id: inst.institution_id, amount: Number(amount), reason });
      toast.success(`+${amount} coins added to ${inst.name}. New balance: ${r.data.balance}`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="inst-credit-modal">
      <div className="bg-white max-w-md w-full border border-zinc-200" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Fund institution wallet</div>
            <div className="font-display text-lg font-bold">{inst.name}</div>
            <div className="text-xs text-zinc-500">Current balance: <span className="font-mono font-bold">{(inst.credits || 0).toLocaleString()}</span> coins</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900" data-testid="inst-credit-cancel"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1 font-semibold">Amount</div>
            <input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)}
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="inst-credit-amount-input" />
            <div className="flex gap-2 mt-2 flex-wrap">
              {[500, 1000, 5000, 10000, 50000].map((v) => (
                <button key={v} onClick={() => setAmount(v)}
                  className="text-xs border border-zinc-300 px-2 py-1 hover:bg-zinc-50"
                  data-testid={`inst-credit-preset-${v}`}>+{v.toLocaleString()}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1 font-semibold">Reason (optional)</div>
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Quarterly allocation"
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="inst-credit-reason-input" />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-zinc-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-zinc-300 text-sm hover:border-zinc-900">Cancel</button>
          <button onClick={submit} disabled={saving}
            className="px-4 py-2 bg-zinc-900 text-white text-sm hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="inst-credit-submit">
            <Coin size={14} weight="fill" /> {saving ? "Adding…" : `Add ${amount || 0}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value, tone = "zinc", icon }) {
  const tones = {
    zinc: "border-zinc-200 text-zinc-900",
    emerald: "border-emerald-200 text-emerald-900",
    red: "border-red-200 text-red-900",
    amber: "border-amber-200 text-amber-900",
  };
  return (
    <div className={`border ${tones[tone]} p-4`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 inline-flex items-center gap-1.5">{icon} {label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}

function LabeledInput({ label, value, onChange, type = "text", mono = false, testId }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none ${mono ? "font-mono" : ""}`}
        data-testid={testId}
      />
    </div>
  );
}
